import React, {useEffect} from 'react';
import {FieldMetaProps} from 'formik';
import {Box, Typography} from '@mui/material';
import {get} from 'lodash';
import {UPDATES} from '../../../shared/UPDATES';
import {SubPlanProps, NewSubPlanDetails} from './NewSubPlanDetails';
import RecommendPlanForm, {
  RecommendPlanFormsStyled,
} from '../CreateRecommend/RecommendSourceDestination/RecommendPlanForm';
import {recommendFieldsType} from '../CreateRecommend/recommendFields';
import type {newPlanFields, newSubplanFields} from './setUpNewFields';
import {Pot, Pot_children} from '../../../schema-types/Pot';
import {useRecommendContext} from '../CreateRecommend/CreateRecommendHelpers';
import usePlanNamesProviders, {
  PlanProviderSelectOption,
} from '../../../hooks/usePlanNamesProviders';
import {
  useAutoForm,
  useAutoField,
  SetFieldValueWithSourceKey,
} from '../../../components/AutoForm/useAutoForm';
import {useShowMandateSelector} from '../../../pages/MandatesPage/MandatesModal';

export const getSavedChildSubPlans = (pots: Pot[], parentId: string): Pot_children[] => {
  if (!pots) {
    return [];
  }
  const parent = pots?.find((p) => p.id === parentId);
  if (!parent) {
    return [];
  }
  const {children} = parent;
  return children || [];
};
export const toggleSubPlanChecked = ({
  id,
  values,
  formikKeyPrefix,
  setFieldValue,
}: {
  id: string;
  values: recommendFieldsType;
  formikKeyPrefix: string;
  setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
}) => {
  const selected: boolean = get(values, `${formikKeyPrefix}.${id}.checked`, false);
  setFieldValue(UPDATES._460, `${formikKeyPrefix}.${id}.checked`, !selected);
};

export const ensureFirstSubPlanSelected = ({
  hasSaltusWrapper,
  formikKeyPrefix,
  setFieldValue,
}: {
  hasSaltusWrapper: boolean;
  formikKeyPrefix: string;
  setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
}) => {
  if (!hasSaltusWrapper) {
    return;
  }
  setFieldValue(UPDATES._461, `${formikKeyPrefix}.subplan0.checked`, true, true);
};

export const useEnsureFirstSubPlanSelected = ({
  hasSaltusWrapper,
  formikKeyPrefix,
}: {
  hasSaltusWrapper: boolean;
  formikKeyPrefix: string;
}) => {
  const {setFieldValue} = useAutoForm<recommendFieldsType>();
  useEffect(() => {
    ensureFirstSubPlanSelected({hasSaltusWrapper, formikKeyPrefix, setFieldValue});
  }, [hasSaltusWrapper]);
};

export const WrappedNewSubPlans: React.FC<{
  formikKeyPrefix: string;
  context: 'setUpNew' | 'transfer' | 'contribute';
}> = ({formikKeyPrefix, context}) => {
  const [subplan0FieldObj, subplan0FieldMeta] = useAutoField<newPlanFields['subplan0']>(
    `${formikKeyPrefix}.subplan0`
  );
  const [subPlan1FieldObj, subPlan1FieldMeta] = useAutoField<newPlanFields['subplan1']>(
    `${formikKeyPrefix}.subplan1`
  );
  const {values, setFieldValue} = useAutoForm<recommendFieldsType>();
  const {productProvidersOptionGroups, isSaltusProvider} = usePlanNamesProviders();

  const [planProviderObj] = useAutoField<newPlanFields['planProvider']>(
    `${formikKeyPrefix}.planProvider`
  );
  const hasSaltusWrapper = isSaltusProvider(planProviderObj.value);
  const planProviderOptionsReduced = productProvidersOptionGroups.reduce<
    PlanProviderSelectOption[]
  >((acc, curr) => [...acc, ...curr.options], []);
  const {showMandateSelector} = useShowMandateSelector({
    formikKeyPrefix,
    sourcePot: undefined,
    planProviderOptions: planProviderOptionsReduced,
  });
  const getSubplanMeta = (
    subplanId: string
  ): FieldMetaProps<newSubplanFields | undefined> =>
    subplanId === 'subplan0' ? subplan0FieldMeta : subPlan1FieldMeta;

  const subPlanIds = ['subplan0'];

  const changeSubPlan = (id: string) => {
    toggleSubPlanChecked({id, values, formikKeyPrefix, setFieldValue});
  };

  useEnsureFirstSubPlanSelected({hasSaltusWrapper, formikKeyPrefix});

  const checkedList: string[] = [];

  if (subplan0FieldObj.value?.checked) {
    checkedList.push('subplan0');
  }
  if (subPlan1FieldObj.value?.checked) {
    checkedList.push('subplan1');
  }
  return (
    <Box ml={2}>
      <RecommendPlanFormsStyled>
        {subPlanIds.map((subplanId, index) => {
          const obj = index === 0 ? subplan0FieldObj.value : subPlan1FieldObj.value;
          return (
            <RecommendPlanForm
              key={subplanId}
              id={subplanId}
              mode="multiple"
              summary={<Typography>{`Subplan ${index + 1} - GIA Gross`}</Typography>}
              validation={{
                totalFields: obj?.checked
                  ? Object.keys(getSubplanMeta(subplanId).value || {}).length
                  : 0,
                invalidFields: Object.keys(getSubplanMeta(subplanId).error || {}).length,
              }}
              reccId=""
              subItems={null}
              details={
                <NewSubPlanDetails
                  showMandateSelector={showMandateSelector || false}
                  planProviderOptionsReduced={planProviderOptionsReduced}
                  disableAmount={context === 'transfer' && index === 0}
                  context={context}
                  formikKeyPrefix={`${formikKeyPrefix}.subplan${index}`}
                />
              }
              isSelected={checkedList.includes(subplanId)}
              isSubplan
              handleChange={changeSubPlan}
            />
          );
        })}
      </RecommendPlanFormsStyled>
    </Box>
  );
};
